import { t } from "@lingui/macro";
import {
  type Brand,
  BrandCreditAccountsWhereColumn,
  SqlOperator,
} from "@src/__generated__/graphql";
import type {
  ProjectModel,
  StatsModel,
} from "@src/components/modules/projects/detail/models";
import { STRIPED_BG } from "@src/components/modules/projects/detail/Stats/billing-box";
import type { BillingMetricColumnRow } from "@src/components/modules/projects/detail/Stats/billing-box/types";
import { now } from "@src/components/ui-kit";
import { getMonths } from "@src/config/constants";
import type { WorkspaceStore } from "@src/stores/WorkspaceStore/WorkspaceStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { HUNDRED_PERCENT } from "@src/utils/formatters";

function toPercent10000(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(
    Math.max((value / total) * HUNDRED_PERCENT, 0),
    HUNDRED_PERCENT,
  );
}

export function createMainSegments(stats: StatsModel) {
  return [
    {
      bg: "teal.500",
      percent: toPercent10000(stats.credit, stats.total_budget),
      name: "consumed",
    },
    {
      bg: STRIPED_BG,
      percent: toPercent10000(
        stats.unused_sum_from_proformas,
        stats.total_budget,
      ),
      name: "proforma",
    },
  ];
}

export function createInvoiceSegments(stats: StatsModel) {
  return [
    {
      bg: "teal.500",
      name: "paid",
      percent: toPercent10000(
        stats.outgoing_invoice_paid,
        stats.outgoing_invoice_amount,
      ),
    },
  ];
}

export function createInvoiceRows(
  project: ProjectModel,
  workspaceStore: WorkspaceStore,
  onPlannedClick: () => void,
): BillingMetricColumnRow[] {
  const rows: BillingMetricColumnRow[] = [
    {
      label: t`Paid`,
      value: project.stats.outgoing_invoice_paid,
      color: "teal.500",
      name: "paid",
    },
  ];

  if (workspaceStore.settings?.moduleSettings.billing_plan) {
    const currentMonth = getMonths()[now().getMonth()];

    rows.push({
      label: `${t`Planned`} (${currentMonth})`,
      value: project.currentMonthBillingPlanOutgoingInvoice,
      name: "planned",
      color: "gray.400",
      onClick: onPlannedClick,
    });
  }

  return rows;
}

export function createProformaSegments(stats: StatsModel) {
  return [
    {
      name: "paid",
      bg: "teal.500",
      percent: toPercent10000(
        stats.outgoing_proforma_invoice_paid,
        stats.outgoing_proforma_invoice_amount,
      ),
    },
  ];
}

export function createProformaRows(
  project: ProjectModel,
  workspaceStore: WorkspaceStore,
  onPlannedClick: () => void,
): BillingMetricColumnRow[] {
  const rows: BillingMetricColumnRow[] = [
    {
      label: t`Paid`,
      value: project.stats.outgoing_proforma_invoice_paid,
      color: "teal.500",
      name: "paid",
    },
  ];

  if (workspaceStore.settings?.moduleSettings.billing_plan) {
    const currentMonth = getMonths()[now().getMonth()];

    rows.push({
      label: `${t`Planned`} (${currentMonth})`,
      value: project.currentMonthBillingPlanOutgoingProformaInvoice,
      color: "gray.400",
      name: "planned",
      onClick: onPlannedClick,
    });
  }

  rows.push(
    {
      label: t`Settled`,
      value: project.stats.outgoing_proforma_invoice_settled,
    },
    {
      label: t`Unsettled (Unused)`,
      value: project.stats.outgoing_proforma_invoice_unsettled,
    },
  );

  return rows;
}

export function createPrepaymentSegments(stats: StatsModel) {
  return [
    {
      name: "used",
      bg: "teal.500",
      percent: toPercent10000(
        stats.credit_from_prepayments - stats.credit_from_unused_prepayments,
        stats.credit_from_prepayments,
      ),
    },
    {
      name: "unused",
      bg: "orange.300",
      percent: toPercent10000(
        stats.credit_from_unused_prepayments,
        stats.credit_from_prepayments,
      ),
    },
  ];
}

export function createPrepaymentRows(
  stats: StatsModel,
  brandId: Brand["id"],
): BillingMetricColumnRow[] {
  const filters = new Filters([
    new Filter({
      column: BrandCreditAccountsWhereColumn.BrandId,
      operator: SqlOperator.In,
      options: [],
      value: [brandId],
    }),
  ]);

  return [
    {
      label: t`Used`,
      name: "used",
      value: stats.credit_from_prepayments,
      color: "teal.500",
    },
    {
      label: t`Unused`,
      name: "unused",
      value: stats.credit_from_unused_prepayments,
      color: "orange.300",
      href: {
        pathname: "/invoices/credit-accounts",
        query: filters.asURLSearchParam,
      },
    },
  ];
}
